import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, CheckCircle2, ClipboardList, Star, ShieldCheck, TrendingUp, ArrowRight, MapPin, IndianRupee, Lock, Clock, AlertCircle } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { studentNav } from "../../components/shared/navConfig";
import { studentStats, tasks, applications } from "../../data/mockData";
import { useAuth } from "../../context/AppContext";

const KYC_UI = {
  approved:    { icon: ShieldCheck,  color: "#10B981", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)", title: "KYC verified",           desc: "You can apply to all tasks.", cta: "Update profile", badge: "Verified" },
  pending:     { icon: Clock,        color: "#F59E0B", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.35)", title: "KYC under review",        desc: "Admin is reviewing your document. You'll be notified within 4 hours.", cta: "View profile", badge: "Pending" },
  rejected:    { icon: AlertCircle,  color: "#F43F5E", bg: "rgba(244,63,94,0.10)",  border: "rgba(244,63,94,0.35)",  title: "KYC rejected",             desc: "Please re-upload a clearer document to unlock task applications.", cta: "Re-upload KYC", badge: "Rejected" },
  not_started: { icon: Lock,         color: "#F59E0B", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.35)", title: "Complete KYC to apply",    desc: "Upload Aadhaar or an equivalent ID to start applying for tasks.", cta: "Start KYC", badge: "Pending" },
};

export default function StudentDashboard() {
  const { user } = useAuth();
  const active = applications.filter(a => a.status === "Allocated");
  const kyc = KYC_UI[user?.kyc_status || "not_started"];
  const KycIcon = kyc.icon;

  return (
    <DashboardShell nav={studentNav} title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`} subtitle="Here's your workspace at a glance.">
      <div className="card-surface p-5 mb-8 flex items-center gap-5 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-fine opacity-60"/>
        <div className="relative w-11 h-11 rounded-md flex items-center justify-center"
             style={{ background: kyc.bg, border: `1px solid ${kyc.border}` }}>
          <KycIcon style={{ color: kyc.color }} size={18}/>
        </div>
        <div className="relative min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm text-white font-medium">
            {kyc.title} <StatusBadge status={kyc.badge}/>
          </div>
          <div className="text-xs text-zinc-500 mt-1">{kyc.desc}</div>
        </div>
        <Link to="/student/onboarding" data-testid="complete-profile-btn" className="btn-ghost px-4 py-2 rounded-full text-xs relative">
          {kyc.cta}
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard testId="stat-profile-completion" label="Profile completion" value="80" suffix="%" delta="+10%" icon={TrendingUp} accent/>
        <StatCard testId="stat-available-tasks"    label="Available tasks"    value={studentStats.availableTasks} delta="+6"   icon={Briefcase}/>
        <StatCard testId="stat-active-tasks"       label="Active tasks"       value={studentStats.activeTasks}    delta="+1"   icon={ClipboardList}/>
        <StatCard testId="stat-completed-tasks"    label="Completed tasks"    value={studentStats.completedTasks} delta="+3"   icon={CheckCircle2}/>
        <StatCard testId="stat-rating"             label="Rating"             value={studentStats.rating}         delta="+0.2" icon={Star}/>
        <StatCard testId="stat-trust-score"        label="Trust score"        value={studentStats.trustScore}     delta="+4"   icon={ShieldCheck}/>
        <StatCard testId="stat-earnings"           label="Total earnings"     value={`₹${(studentStats.earnings/1000).toFixed(1)}k`} delta="+12%" icon={IndianRupee} accent/>
        <StatCard testId="stat-kyc-status"         label="KYC status"         value={kyc.badge} icon={ShieldCheck}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recommended tasks */}
        <div className="lg:col-span-2 card-surface p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="eyebrow">Recommended for you</div>
              <h3 className="font-display text-lg text-white mt-1">Best matches this week</h3>
            </div>
            <Link to="/student/tasks" data-testid="view-all-tasks-link" className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 transition-colors duration-200">View all <ArrowRight size={12}/></Link>
          </div>
          <div className="space-y-3">
            {tasks.slice(0, 4).map(t => (
              <Link key={t.id} to={`/student/tasks/${t.id}`} data-testid={`recommended-task-${t.id}`}
                    className="flex items-center gap-4 p-3 rounded-md border transition-colors duration-200 hover:bg-white/5"
                    style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
                <div className="w-9 h-9 rounded-md flex items-center justify-center font-mono text-[10px] text-blue-400"
                     style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)" }}>
                  {t.matchScore}%
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{t.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{t.company} · <MapPin size={10} className="inline"/> {t.distance}</div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="text-sm text-white font-mono">₹{t.budget.toLocaleString()}</div>
                  <div className="text-[10px] text-zinc-500">{t.deadline}</div>
                </div>
                <StatusBadge status={t.status}/>
              </Link>
            ))}
          </div>
        </div>

        {/* Active workroom */}
        <div className="card-surface p-5">
          <div className="eyebrow">Active engagement</div>
          <h3 className="font-display text-lg text-white mt-1 mb-4">Vendor Payment Recon</h3>
          <img alt="" src="https://images.pexels.com/photos/5324900/pexels-photo-5324900.jpeg?auto=compress&cs=tinysrgb&w=800"
               className="w-full h-32 object-cover rounded-md mb-4 opacity-90"/>
          <div className="space-y-2 text-xs">
            <Row l="Company" v="Kavyansh FoodWorks"/>
            <Row l="Location" v="Koregaon Park, Pune"/>
            <Row l="Budget" v="₹7,400"/>
            <Row l="Start" v="Mar 04 · 10:30 AM"/>
            <Row l="Status" v={<StatusBadge status="Check-in Open"/>}/>
          </div>
          <Link to="/workroom" data-testid="open-workroom-btn" className="btn-primary w-full mt-5 py-2 rounded-md text-xs inline-flex items-center justify-center gap-2">
            Open workroom <ArrowRight size={12}/>
          </Link>
        </div>
      </div>
    </DashboardShell>
  );
}

const Row = ({ l, v }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b last:border-b-0" style={{ borderColor: "var(--border)" }}>
    <span className="text-zinc-500">{l}</span>
    <span className="text-zinc-200 text-right">{v}</span>
  </div>
);
